import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
// Side-effect import: captures Chrome's early `beforeinstallprompt` event at
// module scope so the PWA install buttons work no matter when they mount.
import "@/lib/pwa";
import App from "@/App";

// Disable Grammarly across the whole app — Grammarly's Chrome extension
// injects itself into every textarea, fights with React controlled inputs,
// and drops keystrokes (visible as "one character at a time" bug). Also
// disable other common third-party input enhancers that cause the same.
// Setting these attributes on <body> propagates to all descendants.
if (typeof document !== "undefined" && document.body) {
    document.body.setAttribute("data-gramm", "false");
    document.body.setAttribute("data-gramm_editor", "false");
    document.body.setAttribute("data-enable-grammarly", "false");
    // LangTool, SwiftKey-on-web, etc.
    document.body.setAttribute("data-lt-active", "false");

    // Detect if we're running inside a Capacitor native shell. Grammarly is a
    // BROWSER EXTENSION — it cannot run inside iOS WKWebView or Android
    // WebView. Running a global MutationObserver on native is pure overhead
    // AND it fights with Android IME composition (Gboard, Samsung Keyboard),
    // which causes the infamous "one letter then keyboard drops" bug.
    const isNativeCapacitor = !!(window.Capacitor && typeof window.Capacitor.isNativePlatform === "function" && window.Capacitor.isNativePlatform());

    if (!isNativeCapacitor) {
        // Web only — strip Grammarly's injected elements as they appear.
        // We listen ONLY for childList additions and inspect the newly-added
        // node (not the whole DOM) — keeps cost O(insertions) not O(DOM).
        try {
            const GRAMMARLY_TAGS = new Set([
                "GRAMMARLY-EXTENSION",
                "GRAMMARLY-GHOST",
                "GRAMMARLY-DESKTOP-INTEGRATION",
            ]);
            const obs = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    for (const node of m.addedNodes) {
                        if (node.nodeType !== 1) continue;
                        if (GRAMMARLY_TAGS.has(node.tagName) ||
                            node.hasAttribute?.("data-grammarly-shadow-root")) {
                            node.remove();
                        }
                    }
                }
            });
            obs.observe(document.documentElement, { childList: true, subtree: true });
        } catch (e) {
            // Silent — observer isn't critical
        }
    }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>,
);

// Register PWA service worker — installable on iOS / Android home screens.
// When the SW activates a new version, it broadcasts SW_UPDATED to all open
// clients. We auto-reload the page (once) so users always run the latest
// JS bundle instead of a stale cached one.
if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
    let reloadedForSwUpdate = false;
    navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "SW_UPDATED" && !reloadedForSwUpdate) {
            reloadedForSwUpdate = true;
            // Use a tiny timeout so any in-flight UI work finishes first.
            setTimeout(() => window.location.reload(), 100);
        }
    });
}
